import { Container, Graphics, Text } from 'pixi.js';
import { ENTRY_BELL } from '@duck/contracts';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { registerScenery, synthCtx } from '../scenery';

/**
 * ENTRYWAY (Прихожая) — the WIDENED front-door vestibule where every citizen "arrives" (Floorplan v2 /
 * ADR-0022). The brief: feel like a warm, tidy front hall the instant you spawn. The room just grew
 * LEFT, so it now reads as a real welcome hall with two clear halves: a HERO north wall (the door, a
 * prominent wall DOORBELL + a "BELL →" sign that points right at it, flanked by a symmetric pair of
 * plants and a framed "Welcome" picture) and a LEFT WAITING NOOK (a low sittable bench along the new
 * left wall, a coat rack + umbrella stand in the corner). A coir welcome mat grounds the door where you
 * land. Axis-aligned, one neat rug under the bench, deliberate corners — cozy, never cluttered.
 *
 * Geometry (NEW zone x[440..1040] y[1000..1160], 600×160, centre 740,1080):
 *   - the ONLY door is entryway-N on the NORTH wall → opening x[740..860] (up into reception); that lane
 *     (x740..860) stays clear top-to-bottom so a duck walks straight in/out;
 *   - the BELL affordance is at {@link ENTRY_BELL} (920,1040) — the DOORBELL is drawn EXACTLY there, on
 *     the north wall just RIGHT of the door, with the "BELL" sign a touch further right pointing back at it;
 *   - everyone spawns near (800,1080); the central lane stays walkable, fixtures hug the side/north walls.
 * Built ONCE on map build/re-skin — no animation, no per-frame work.
 */

// ── EDITABLE SCENERY: register this room's bespoke furniture so the level editor can move/add/re-render it.
registerScenery('waiting-bench', (art) => waitingBench(synthCtx(art), 96), 'Waiting bench', '🛋️');
registerScenery('coat-rack', (art) => coatRack(synthCtx(art)), 'Coat rack', '🧥');
registerScenery('umbrella-stand', (art) => umbrellaStand(synthCtx(art)), 'Umbrella stand', '☂️');

/** A seat the decorator publishes so the sit system can anchor a duck onto a bench/chair we drew. */
export interface SeatAnchor {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Facing in radians (0 = +x / east, Math.PI/2 = +y / south/down, etc.). */
  readonly facing: number;
  readonly kind: 'chair' | 'sofa';
}

/** A WELCOME MAT: a warm coir rectangle with a lighter inset border + a couple of tread stripes. */
function welcomeMat(ctx: RoomContext, w: number, h: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const coir = mix(0x8a6a3a, art.wall, 0.25); // warm doormat fibre
  const g = new Graphics()
    .roundRect(-w / 2, -h / 2, w, h, 5)
    .fill({ color: darken(coir, 0.05) })
    .roundRect(-w / 2 + 5, -h / 2 + 5, w - 10, h - 10, 4)
    .stroke({ color: lighten(coir, 0.22), width: 2, alpha: 0.7 });
  for (let i = 1; i <= 3; i++) {
    const yy = -h / 2 + (h * i) / 4;
    g.moveTo(-w / 2 + 10, yy).lineTo(w / 2 - 10, yy).stroke({ color: darken(coir, 0.12), width: 1.5, alpha: 0.5 });
  }
  c.addChild(g);
  return c;
}

/**
 * THE DOORBELL (hero element) — a PROMINENT wall-mounted bell unit: a mounted button box with a brass
 * bell dome on top, a glowing press button, and a little speaker grille below. Reads clearly as "a
 * doorbell on the wall". Origin at its centre (placed EXACTLY at {@link ENTRY_BELL}, 920,1040); ~32×46.
 */
function doorbell(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const plate = mix(0xb9c0c9, art.wall, 0.18); // brushed metal housing
  const brass = 0xe7c66b;
  const grille = darken(plate, 0.32);
  c.addChild(
    new Graphics()
      // contact shadow on the wall (housing reads as mounted/proud of the wall)
      .roundRect(-16, -23, 34, 50, 8)
      .fill({ color: 0x000000, alpha: 0.2 })
      // the housing plate
      .roundRect(-15, -24, 30, 48, 7)
      .fill(plate)
      // top sheen + screw dimples in the four corners (a real bolted-on box)
      .roundRect(-15, -24, 30, 14, 7)
      .fill({ color: 0xffffff, alpha: 0.16 })
      .circle(-10, -19, 1.3)
      .fill(darken(plate, 0.3))
      .circle(10, -19, 1.3)
      .fill(darken(plate, 0.3))
      .circle(-10, 19, 1.3)
      .fill(darken(plate, 0.3))
      .circle(10, 19, 1.3)
      .fill(darken(plate, 0.3)),
  );
  // the brass bell dome on top — the unmistakable "bell".
  c.addChild(
    new Graphics()
      .arc(0, -8, 9, Math.PI, Math.PI * 2)
      .fill(brass)
      .arc(0, -8, 9, Math.PI, Math.PI * 2)
      .stroke({ color: darken(brass, 0.25), width: 1.4 })
      // bright highlight across the dome
      .arc(-2, -9, 5, Math.PI * 1.05, Math.PI * 1.75)
      .stroke({ color: lighten(brass, 0.3), width: 2, alpha: 0.9 })
      // the bell's clapper knob hanging at the rim
      .circle(0, -1, 2)
      .fill(darken(brass, 0.32))
      // the little striker/yoke bar across the top
      .roundRect(-10, -9, 20, 2.5, 1)
      .fill(darken(brass, 0.18)),
  );
  // the glowing press BUTTON (the affordance you tap) — a haloed ring around a lit dot.
  c.addChild(
    new Graphics()
      .circle(0, 8, 7)
      .fill({ color: art.glow, alpha: 0.18 }) // soft halo so it reads as "press me / it's live"
      .circle(0, 8, 5)
      .fill(darken(plate, 0.28))
      .circle(0, 8, 3.4)
      .fill({ color: art.glow, alpha: 0.95 })
      .circle(-1, 7, 1.2)
      .fill({ color: lighten(art.glow, 0.3), alpha: 0.95 }),
  );
  // a little speaker GRILLE below the button (three slots) — the "intercom" cue.
  c.addChild(
    new Graphics()
      .roundRect(-7, 15, 14, 6, 2)
      .fill(grille)
      .roundRect(-5, 16.2, 10, 1, 0.5)
      .fill({ color: 0x000000, alpha: 0.35 })
      .roundRect(-5, 18, 10, 1, 0.5)
      .fill({ color: 0x000000, alpha: 0.35 }),
  );
  return c;
}

/**
 * THE "BELL" SIGN (hero element) — a small framed plate reading "BELL" with a little pointer/arrow aimed
 * LEFT at the doorbell, so a newcomer instantly sees where to ring. Origin at its centre.
 */
function bellSign(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const frame = mix(0x6f4a28, art.wall, 0.2); // warm wood frame
  const plate = lighten(mix(0xf3ead2, art.wall, 0.12), 0.04); // cream sign face
  const ink = darken(art.accent, 0.35);
  c.addChild(
    new Graphics()
      // contact shadow
      .roundRect(-23, -13, 48, 28, 5)
      .fill({ color: 0x000000, alpha: 0.16 })
      // wood frame + cream face
      .roundRect(-23, -14, 46, 26, 5)
      .fill(frame)
      .roundRect(-20, -11, 40, 20, 3)
      .fill(plate)
      .roundRect(-20, -11, 40, 6, 3)
      .fill({ color: 0xffffff, alpha: 0.25 }),
  );
  // the word "BELL" rendered as crisp text on the sign face.
  const label = new Text({
    text: 'BELL',
    style: {
      fontFamily: 'Arial',
      fontSize: 13,
      fontWeight: '700',
      letterSpacing: 1.5,
      fill: ink,
    },
  });
  label.anchor.set(0.5);
  label.x = 3; // nudge right to leave room for the arrow on the left
  label.y = -1;
  c.addChild(label);
  // a little pointer/arrow on the left edge, aiming back toward the doorbell.
  c.addChild(
    new Graphics()
      .poly([-26, 0, -17, -6, -17, 6])
      .fill(ink)
      .moveTo(-17, 0)
      .lineTo(-13, 0)
      .stroke({ color: ink, width: 2.4 }),
  );
  return c;
}

/** A COAT RACK: a slim pole with a round base, three hooks, and a coat + scarf hanging — a homey touch. */
function coatRack(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const wood = mix(0x6f4a28, art.wall, 0.18);
  const coat = mix(art.accent, 0x4a5a7a, 0.4);
  c.addChild(
    new Graphics()
      .ellipse(0, 6, 12, 5)
      .fill({ color: 0x000000, alpha: 0.16 })
      // base + pole
      .ellipse(0, 4, 11, 4)
      .fill(darken(wood, 0.1))
      .roundRect(-2.5, -54, 5, 58, 2)
      .fill(wood)
      .roundRect(-2.5, -54, 2, 58, 2)
      .fill({ color: 0xffffff, alpha: 0.16 }),
  );
  // three hooks near the top
  const hooks = new Graphics();
  for (const hx of [-9, 0, 9]) {
    hooks
      .moveTo(0, -48)
      .lineTo(hx, -44)
      .stroke({ color: darken(wood, 0.15), width: 2.4 })
      .circle(hx, -44, 1.8)
      .fill(lighten(wood, 0.2));
  }
  c.addChild(hooks);
  // a hanging coat on the left hook + a scarf on the right
  c.addChild(
    new Graphics()
      .moveTo(-9, -44)
      .bezierCurveTo(-20, -30, -18, -8, -10, -4)
      .bezierCurveTo(-4, -8, -2, -30, -9, -44)
      .fill(coat)
      .moveTo(-9, -44)
      .lineTo(-12, -20)
      .stroke({ color: darken(coat, 0.18), width: 1.5, alpha: 0.7 })
      // scarf
      .roundRect(7, -42, 4, 26, 2)
      .fill(lighten(art.accent, 0.1)),
  );
  return c;
}

/** An UMBRELLA STAND: a little tub with two umbrellas leaning — the believable detail by the door. */
function umbrellaStand(ctx: RoomContext): Container {
  const { art, lighten, darken, mix } = ctx;
  const c = new Container();
  const tub = mix(0x8a9099, art.wall, 0.22);
  c.addChild(
    new Graphics()
      .ellipse(0, 16, 13, 5)
      .fill({ color: 0x000000, alpha: 0.16 })
      // tub
      .roundRect(-11, -4, 22, 22, 4)
      .fill(tub)
      .roundRect(-11, -4, 22, 6, 4)
      .fill({ color: 0x000000, alpha: 0.18 })
      .roundRect(-11, -4, 5, 22, 4)
      .fill({ color: 0xffffff, alpha: 0.14 })
      // two umbrellas (canes + handles)
      .moveTo(-4, -2)
      .lineTo(-9, -34)
      .stroke({ color: darken(art.accent, 0.1), width: 3 })
      .moveTo(-9, -34)
      .bezierCurveTo(-13, -34, -13, -29, -10, -29)
      .stroke({ color: darken(art.accent, 0.1), width: 3 })
      .moveTo(4, -2)
      .lineTo(8, -30)
      .stroke({ color: lighten(0x4a7a5a, 0.05), width: 3 })
      .moveTo(8, -30)
      .bezierCurveTo(12, -30, 12, -26, 9, -26)
      .stroke({ color: lighten(0x4a7a5a, 0.05), width: 3 }),
  );
  return c;
}

/**
 * A low WAITING BENCH for the left nook — a warm wooden bench with a cushioned top and turned legs, sized
 * to seat two while waiting to be received. Origin at the SEAT CENTRE (so a duck "sits" right on the
 * cushion). Faces DOWN/into the room (its back is to the left wall). ~96 wide.
 */
function waitingBench(ctx: RoomContext, w: number): Container {
  const { art, mix, darken } = ctx;
  const c = new Container();
  const wood = mix(0x7a4f2c, art.wall, 0.16);
  const cushion = mix(art.accent, 0x9a6a5a, 0.35);
  const half = w / 2;
  c.addChild(
    new Graphics()
      // contact shadow
      .ellipse(0, 14, half, 9)
      .fill({ color: 0x000000, alpha: 0.16 }),
  );
  c.addChild(
    new Graphics()
      // backrest slats (against the wall, low so it never blocks the lane)
      .roundRect(-half, -26, w, 10, 4)
      .fill(darken(wood, 0.12))
      .roundRect(-half, -26, w, 3, 4)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // the wooden bench frame / seat plank
      .roundRect(-half, -6, w, 16, 5)
      .fill(wood)
      .roundRect(-half, -6, w, 5, 5)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // turned legs
      .roundRect(-half + 5, 8, 6, 8, 2)
      .fill(darken(wood, 0.2))
      .roundRect(half - 11, 8, 6, 8, 2)
      .fill(darken(wood, 0.2)),
  );
  // a soft cushion split into two seat pads (the two sit slots)
  c.addChild(
    new Graphics()
      .roundRect(-half + 4, -12, half - 6, 12, 5)
      .fill(cushion)
      .roundRect(-half + 4, -12, half - 6, 4, 5)
      .fill({ color: 0xffffff, alpha: 0.14 })
      .roundRect(2, -12, half - 6, 12, 5)
      .fill(cushion)
      .roundRect(2, -12, half - 6, 4, 5)
      .fill({ color: 0xffffff, alpha: 0.14 })
      // piped seam between the pads
      .moveTo(0, -11)
      .lineTo(0, -1)
      .stroke({ color: darken(cushion, 0.22), width: 1.5, alpha: 0.6 }),
  );
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext): SeatAnchor[] => {
  const { zone, art, f } = ctx;
  const left = zone.x; // 440
  const right = zone.x + zone.width; // 1040
  const top = zone.y; // 1000
  const bottom = zone.y + zone.height; // 1160
  const cx = ctx.cx; // 740 (also the door centre: door opening x[740..860])
  const doorCx = 800; // visual centre of the door opening (x740..860)

  const seats: SeatAnchor[] = [];

  // ── LEFT WAITING NOOK: one neat rug grounding the bench group, inset from the walls. ────────────────
  // Sized to the bench (~96 wide); centred on the bench seat, hugging the left wall, clear of the lane.
  place(ctx, f.rug(art, 140, 84), left + 86, bottom - 50);

  // ── WAITING BENCH along the NEW left wall — a real two-seat waiting spot (faces DOWN into the room). ─
  // Seat centre at (520,1085); back to the left wall. Two sit pads at x≈500 and x≈540, both ≥24px inside
  // walls (left wall x440, bottom wall y1160) and clear of the door lane (x740..860).
  const benchX = 520;
  const benchY = 1085;
  place(ctx, waitingBench(ctx, 96), benchX, benchY, 'waiting-bench');
  // Two sit anchors on the cushion pads, facing DOWN (+y) into the room.
  seats.push({ id: 'entryway-bench-l', x: benchX - 22, y: benchY, facing: Math.PI / 2, kind: 'sofa' });
  seats.push({ id: 'entryway-bench-r', x: benchX + 22, y: benchY, facing: Math.PI / 2, kind: 'sofa' });

  // ── LEFT CORNER: a coat rack + umbrella stand — the homey "drop your things" corner (deep SW). ──────
  place(ctx, coatRack(ctx), left + 30, bottom - 40, 'coat-rack');
  place(ctx, umbrellaStand(ctx), left + 64, bottom - 26, 'umbrella-stand');

  // ── WELCOME MAT under the door, where you land + leave (centred on the door opening, just inside). ──
  place(ctx, welcomeMat(ctx, 150, 70), doorCx, top + 64);

  // ── HERO 1 — THE DOORBELL on the wall, EXACTLY at the bell affordance (so "Ring the bell" is literal). ─
  place(ctx, doorbell(ctx), ENTRY_BELL.x, ENTRY_BELL.y); // (920,1040), north wall right of the door

  // ── HERO 2 — THE "BELL →" SIGN, a touch right of the doorbell, its arrow pointing back at it. ────────
  place(ctx, bellSign(ctx), ENTRY_BELL.x + 50, ENTRY_BELL.y + 6); // (970,1046)

  // ── Symmetric PAIR of plants flanking the door (left jamb + right side), framing the entrance. ──────
  // Left of the door gap (door starts x740); right one tucked between the door and the doorbell (x860..920).
  place(ctx, f.plant(art, 'tall'), cx - 30, top + 34); // ~710 — left jamb
  place(ctx, f.plant(art, 'tall'), right - 150, top + 34); // ~890 — right jamb, clear of the bell unit

  // ── A framed "Welcome" picture on the NORTH wall, in the open left half above the bench nook. ───────
  place(ctx, f.wallArt(art, 'poster'), left + 150, top + 22); // ~590 — balances the bell signage on the right

  return seats;
};

export default decorate;
